/**
 * BillingReturnPage — landing page after Stripe Checkout redirects back.
 *
 * Stripe sends the browser to `/billing/success` or `/billing/cancel` per the
 * URLs we passed into Session.create. This page:
 *   - Refreshes BillingContext so the new plan / balance shows immediately.
 *   - Surfaces a toast explaining what happened.
 *   - Redirects to `/` so the user lands back in the app, not on a dead URL.
 *
 * The webhook (`invoice.paid` → grant monthly allowance) does the authoritative
 * balance update asynchronously. This page just nudges the frontend to re-fetch
 * once Stripe says it's done — if the webhook hasn't arrived yet, the next
 * fetch after a few seconds will pick it up.
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

import { useBilling } from '../../context/BillingContext';
import { useNotification } from '../../context/NotificationContext';

export default function BillingReturnPage({ type }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useBilling();
  const { showSuccess, showInfo } = useNotification();

  // Guard so StrictMode double-invocation doesn't fire the toast twice.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const sessionId = searchParams.get('session_id');

    if (type === 'success') {
      // Give the webhook a brief head start so the first refresh is more likely
      // to pick up the new state. Non-blocking — if webhook is slow, the next
      // manual action or BillingContext refresh will catch up.
      const t = setTimeout(() => refresh(), 1500);
      showSuccess('Payment successful. Your credits will appear shortly.');
      // eslint-disable-next-line no-console
      if (sessionId) console.info('Stripe checkout session completed:', sessionId);
      navigate('/', { replace: true });
      return () => clearTimeout(t);
    }

    // cancel
    showInfo('Checkout canceled. No charge was made.');
    navigate('/', { replace: true });
    return undefined;
  }, [type, refresh, showSuccess, showInfo, navigate, searchParams]);

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        {type === 'success' ? 'Finalizing your subscription…' : 'Returning you to the app…'}
      </Typography>
    </Box>
  );
}
